import { useEffect, useState } from "react";
import api, { formatTL } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { PageHeader, KpiCard, Panel, EmptyState } from "../../components/common";
import { Trophy, Star, Clock, HardHat, CheckCircle2, AlertCircle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Performance() {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/performance", { params: { days } }).then((r) => setData(r.data)); }, [days]);
  if (!data) return <div className="text-slate-500">Yükleniyor...</div>;
  const t = data.technicians;
  const sum = (k) => t.reduce((a, x) => a + (x[k] || 0), 0);
  const rated = t.filter((x) => x.rating_count > 0);
  const avgRating = rated.length ? (rated.reduce((a, x) => a + x.avg_rating, 0) / rated.length).toFixed(2) : "-";
  const withHrs = t.filter((x) => x.avg_hours > 0);
  const avgHrs = withHrs.length ? (withHrs.reduce((a, x) => a + x.avg_hours, 0) / withHrs.length).toFixed(1) : "-";

  return (
    <div className="space-y-6" data-testid="performance-page">
      <PageHeader eyebrow="İş Zekası" title="Teknisyen Performansı" subtitle="Tamamlanan iş, ciro, müşteri puanı ve ortalama çözüm süresi.">
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7">Son 7 gün</SelectItem><SelectItem value="30">Son 30 gün</SelectItem><SelectItem value="90">Son 90 gün</SelectItem><SelectItem value="365">Son 1 yıl</SelectItem></SelectContent>
        </Select>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={CheckCircle2} label="Tamamlanan İş" value={sum("completed")} tone="emerald" />
        <KpiCard icon={Trophy} label="Toplam Ciro" value={formatTL(sum("revenue"))} tone="blue" />
        <KpiCard icon={Star} label="Ort. Müşteri Puanı" value={avgRating} tone="amber" />
        <KpiCard icon={Clock} label="Ort. Çözüm Süresi" value={avgHrs === "-" ? "-" : `${avgHrs} sa`} hint="kayıttan tamamlanmaya" tone="violet" />
      </div>
      {data.unassigned > 0 && (
        <div className="flex items-center gap-2 text-sm p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900"><AlertCircle className="h-4 w-4" />Bu dönemde teknisyen atanmamış {data.unassigned} servis kaydı var.</div>
      )}
      <Panel title="Tamamlanan İş Karşılaştırması">
        {t.length === 0 ? <EmptyState icon={HardHat} title="Teknisyen yok" /> : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={t}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="completed" name="Tamamlanan" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="open" name="Açık" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cancelled" name="İptal" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>
      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="performance-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">#</th><th className="px-4 py-3">Teknisyen</th><th className="px-4 py-3 text-right">Atanan</th><th className="px-4 py-3 text-right">Tamamlanan</th><th className="px-4 py-3 text-right">Tamamlama</th><th className="px-4 py-3 text-right">İptal</th><th className="px-4 py-3 text-right">Ciro</th><th className="px-4 py-3 text-right">Ort. Sepet</th><th className="px-4 py-3 text-right">Puan</th><th className="px-4 py-3 text-right">Ort. Süre</th></tr>
            </thead>
            <tbody>
              {t.map((x, i) => (
                <tr key={x.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3"><span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-400 text-white" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span></td>
                  <td className="px-4 py-3 font-semibold">{x.name}{!x.is_active && <span className="ml-2 text-[10px] text-slate-400">(pasif)</span>}</td>
                  <td className="px-4 py-3 text-right font-mono">{x.assigned}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{x.completed}</td>
                  <td className="px-4 py-3 text-right font-mono">%{x.completion_rate}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-600">{x.cancelled}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatTL(x.revenue)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatTL(x.avg_ticket)}</td>
                  <td className="px-4 py-3 text-right font-mono">{x.avg_rating ? <span className="text-amber-600">★ {x.avg_rating}<span className="text-slate-400 text-xs"> ({x.rating_count})</span></span> : "-"}</td>
                  <td className="px-4 py-3 text-right font-mono">{x.avg_hours ? `${x.avg_hours} sa` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
